import { makeStyles, TextField, TextFieldProps } from "@material-ui/core";
import Autocomplete, { AutocompleteProps } from "@material-ui/lab/Autocomplete";
import classNames from "classnames";
import { noop, omit, pick } from "lodash";
import React, { useMemo, useState } from "react";

function countryToFlag(isoCode: string) {
  const countryIsoCode = isoCode.match(/^[a-z]{2}/i)?.[0];
  return typeof String.fromCodePoint !== "undefined" &&
    countryIsoCode !== undefined
    ? countryIsoCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(char.charCodeAt(0) + 127397)
        )
    : countryIsoCode;
}

const useStyles = makeStyles({
  option: {
    fontSize: 15,
    "& > span": {
      marginRight: 10,
      fontSize: 18,
    },
  },
  root: {
    "& .MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot": {
      paddingRight: "3em",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: 0,
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-endAdornment": {
      right: "1em",
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input:first-child": {
      paddingLeft: "1.2em",
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "1em",
    },
  },
  fullWidth: {
    width: "100%",
  },
});

interface CustomCountryPropsSingle {
  multiple?: false;
  onChange?: (code: string | null) => void;
  value?: string;
  jurisdictions: { code: string; name: string }[];
}

type CountryProps = Omit<
  AutocompleteProps<CountryType, false, boolean, false> & TextFieldProps,
  keyof CustomCountryPropsSingle | "renderInput" | "options"
> &
  CustomCountryPropsSingle;

const textFieldProps = ["label", "error", "helperText", "ref"] as const;

export const KYBJurisdiction = React.forwardRef<HTMLInputElement, CountryProps>(
  (props: CountryProps, ref) => {
    const classes = useStyles();
    const { onChange = noop, fullWidth, className } = props;
    const [tempState, setTempState] = useState<CountryType | null>(
      (() => {
        if (!props.value) return null;
        const option = props.jurisdictions.find((x) => x.code === props.value);
        if (!option) return null;

        return {
          code: props.value,
          label: option.name,
        };
      })()
    );

    const textProps = useMemo(() => pick(props, textFieldProps), [props]);
    const autoCompleteProps = useMemo(() => omit(props, [...textFieldProps]), [
      props,
    ]);

    return (
      <Autocomplete<CountryType, false, boolean, false>
        {...autoCompleteProps}
        freeSolo={false}
        options={props.jurisdictions.map(({ code, name }) => ({
          code,
          label: name,
        }))}
        classes={{
          option: classes.option,
          root: classNames(
            fullWidth && classes.fullWidth,
            classes.root,
            className
          ),
        }}
        autoHighlight
        getOptionSelected={(option: CountryType, value: CountryType) =>
          option.code === value.code
        }
        getOptionLabel={(option) => option.label}
        renderOption={(option) => (
          <>
            <span>{countryToFlag(option.code)}</span>
            {option.label}
          </>
        )}
        value={tempState}
        onChange={(_, bar) => {
          setTempState(bar);
          if (bar === null) {
            onChange(null);
          } else if (Array.isArray(bar)) {
            onChange(bar.map((b) => b.code));
          } else {
            onChange(bar?.code);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...textProps}
            {...params}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
            }}
          />
        )}
      />
    );
  }
);

interface CountryType {
  code: string;
  label: string;
}
